import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	CUSTOM_ELEMENTS_SCHEMA,
	EventEmitter,
	Inject,
	Input,
	OnChanges,
	OnDestroy,
	Output,
	SimpleChanges
} from '@angular/core';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import type { ActivityModel } from 'go-modules/models/activity/activity.schema';
import type { MarkerSet, Marker } from '@go-modules/interfaces/markers/marker';
import { PanelModule } from '@go-modules/components/panel/panel.module';
import { NgxToolbarModule } from '@go-modules/components/toolbar/toolbar.module';
import { NgxToolbarButtonModule } from '@go-modules/components/toolbar/toolbar-button/toolbar-button.module';
import { MarkerDisplayModule } from '@go-modules/components/marker-display/marker-display.module';
import { GoModalService } from '@go-modules/services/go-modal/go-modal.service';
import { MarkerSetDialogComponent } from '@go-modules/components/dialogs/marker-set-dialog/marker-set-dialog.component';
import { NgxGoToastService } from '@go-modules/services/go-toast/go-toast.service';
import { GoToastStatusType } from '@go-modules/enums/go-toast-status-type';
import { Action } from '@go-modules/components/toolbar/toolbar-button/action-to-icon-class-map';
import { tagSetModelToken } from 'go-modules/models/tag-set/tag-set.factory';
import { tagModelToken } from 'go-modules/models/tag/tag.factory';
import { firstValueFrom } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '@go-modules/components/dialogs/confirm-dialog/confirm-dialog.component';

interface LegacyMarkerSet extends MarkerSet {
	$promise?: Promise<MarkerSet>;
}

interface TagSetModelNg1 {
	new (config: Partial<MarkerSet>): LegacyMarkerSet;
	canUpdate(params: { id: number }): { $promise: Promise<{ can_update: boolean }> };
}

type TagModelNg1 = new (config: Partial<Marker>) => Marker;

const enum MarkerSetModalMode {
	EDIT_EXISTING = 'edit-existing',
	EDIT_COPY = 'edit-copy'
}

export function isLegacyMarkerSet (value: unknown): value is LegacyMarkerSet {
	return Boolean((value as LegacyMarkerSet)?.$promise);
}

export function isPromiseLike<T> (value: unknown): value is Promise<T> {
	return typeof (value as Promise<T>)?.then === 'function';
}

@Component({
	selector: 'marker-set-panel',
	standalone: true,
	imports: [
		TranslateModule,
		PanelModule,
		NgxToolbarModule,
		NgxToolbarButtonModule,
		MarkerDisplayModule
	],
	templateUrl: './marker-set-panel.component.html',
	styleUrls: ['./marker-set-panel.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class MarkerSetPanelComponent implements OnChanges, OnDestroy {
	@Input() public markerSet: LegacyMarkerSet | Promise<MarkerSet> | null | undefined;
	@Input() public activity: ActivityModel | null | undefined;
	@Output() public onEdited: EventEmitter<{ markerSet: MarkerSet }> = new EventEmitter();
	@Output() public onRemoved: EventEmitter<void> = new EventEmitter();

	public isLoading: boolean = false;
	public isFetchingEditPermissions: boolean = false;
	public resolvedMarkerSet: MarkerSet | null = null;
	public markers: Marker[] = [];
	public readonly toolbarActions = Action;
	private isDestroyed = false;
	private markerSetLoadRequestId = 0;
	private markerSetActionRequestId = 0;

	constructor (
		private readonly cdr: ChangeDetectorRef,
		private readonly translate: TranslateService,
		private readonly goModalService: GoModalService,
		private readonly ngxGoToastService: NgxGoToastService,
		private readonly dialog: MatDialog,
		@Inject(tagSetModelToken) private readonly tagSetModel: TagSetModelNg1,
		@Inject(tagModelToken) private readonly tagModel: TagModelNg1
	) {}

	public ngOnChanges (changes: SimpleChanges): void {
		if (changes.markerSet) {
			this.markerSetActionRequestId++;
			this.isFetchingEditPermissions = false;
			this.loadMarkerSet().catch(() => {});
		}
	}

	public ngOnDestroy (): void {
		this.isDestroyed = true;
		this.markerSetLoadRequestId++;
		this.markerSetActionRequestId++;
	}

	public async handleEdit (): Promise<void> {
		const markerSetId = this.resolvedMarkerSet?.id;
		if (!markerSetId) {
			return;
		}

		const requestId = ++this.markerSetActionRequestId;
		this.isFetchingEditPermissions = true;
		this.cdr.markForCheck();

		try {
			const response = await this.tagSetModel
				.canUpdate({ id: markerSetId }).$promise;

			if (!this.isCurrentActionRequest(requestId, markerSetId)) {
				return;
			}

			if (response?.can_update) {
				await this.openMarkerSetModal(MarkerSetModalMode.EDIT_EXISTING, markerSetId);
			} else {
				await this.openConfirmCopyFlow(markerSetId);
			}
		} catch {
			if (this.isCurrentActionRequest(requestId, markerSetId)) {
				this.ngxGoToastService.createToast({
					type: GoToastStatusType.ERROR,
					message: 'marker-modifications-permission-error'
				});
			}
		} finally {
			if (this.isCurrentActionRequest(requestId)) {
				this.isFetchingEditPermissions = false;
				this.cdr.markForCheck();
			}
		}
	}

	public async handleRemove (): Promise<void> {
		const markerSet = this.resolvedMarkerSet;
		if (!markerSet) {
			return;
		}

		const markerSetId = markerSet.id;
		const requestId = this.markerSetActionRequestId;
		const isDefaultAccount = this.activity?.name === '__ACCT_DEFAULT__';
		const title = markerSet.title ?? '';

		const dialogRef = ConfirmDialogComponent.open(this.dialog, {
			data: {
				title: this.translate.instant('modal-remove-marker-set_remove-markers'),
				message: isDefaultAccount ?
					this.translate.instant('modal-remove-marker-set_sure-remove-default', { name: title }) :
					this.translate.instant('modal-remove-marker-set_sure-remove', { name: title }),
				note: this.translate.instant('modal-remove-marker-set_note'),
				confirmText: this.translate.instant('common_remove'),
				cancelText: this.translate.instant('common_cancel')
			}
		});

		const confirmed = await firstValueFrom(dialogRef.afterClosed());
		if (confirmed && this.isCurrentActionRequest(requestId, markerSetId)) {
			this.onRemoved.emit();
		}
	}

	private async loadMarkerSet (): Promise<void> {
		const requestId = ++this.markerSetLoadRequestId;
		const markerSet = this.markerSet;

		if (!markerSet) {
			this.resolvedMarkerSet = null;
			this.markers = [];
			this.cdr.markForCheck();
			return;
		}

		this.isLoading = true;
		this.cdr.markForCheck();

		try {
			const resolved = await this.awaitMarkerSetResolution(markerSet);
			if (this.isCurrentLoadRequest(requestId, markerSet)) {
				this.resolvedMarkerSet = resolved ?? null;
				this.sortResolvedMarkerTags();
			}
		} finally {
			if (this.isCurrentLoadRequest(requestId, markerSet)) {
				this.isLoading = false;
				this.cdr.markForCheck();
			}
		}
	}

	private async awaitMarkerSetResolution (
		markerSet: LegacyMarkerSet | Promise<MarkerSet>
	): Promise<MarkerSet> {
		if (isLegacyMarkerSet(markerSet) && markerSet.$promise) {
			const result = await markerSet.$promise;
			return result ?? markerSet;
		}

		if (isPromiseLike<MarkerSet>(markerSet)) {
			return await markerSet;
		}

		return markerSet as MarkerSet;
	}

	private async openMarkerSetModal (mode: MarkerSetModalMode, expectedMarkerSetId?: number): Promise<void> {
		const requestId = this.markerSetActionRequestId;
		const markerSet = this.resolvedMarkerSet;
		if (!markerSet || !this.isExpectedMarkerSet(expectedMarkerSetId)) {
			return;
		}

		const modalData = mode === MarkerSetModalMode.EDIT_EXISTING
			? { markerSet }
			: {
				markerSet: this.buildMarkerSetCopy(markerSet),
				copying: true
			};

		const dialogRef = this.goModalService.open(MarkerSetDialogComponent, false, {
			data: modalData,
			focusReturnSelector: '.marker-set-panel .edit-btn .toolbar-inner-button'
		});

		const result = await firstValueFrom(dialogRef.afterClosed());

		if (
			!this.isCurrentActionRequest(requestId, expectedMarkerSetId) ||
			!result ||
			result.dismissed
		) {
			return;
		}

		this.markerSet = result;
		this.resolvedMarkerSet = result;
		this.sortResolvedMarkerTags();
		this.onEdited.emit({ markerSet: this.resolvedMarkerSet });
		this.cdr.markForCheck();
	}

	private buildMarkerSetCopy (markerSet: MarkerSet | null = this.resolvedMarkerSet): MarkerSet {
		const tags = this.markers.map((tag) => new this.tagModel({
			tag_abbreviation: tag.tag_abbreviation,
			tag_color: tag.tag_color,
			tag_name: tag.tag_name
		}));

		const title = this.translate.instant('common_copy-of', {
			title: markerSet?.title ?? ''
		});

		return new this.tagSetModel({
			tags,
			title
		});
	}

	private async openConfirmCopyFlow (expectedMarkerSetId?: number): Promise<void> {
		const requestId = this.markerSetActionRequestId;
		const dialogRef = ConfirmDialogComponent.open(this.dialog, {
			data: {
				title: this.translate.instant('marker-modifications-disabled'),
				message: this.translate.instant('marker-modifications-disabled-description'),
				confirmText: this.translate.instant('common_create-copy'),
				cancelText: this.translate.instant('common_cancel')
			}
		});

		const confirmed = await firstValueFrom(dialogRef.afterClosed());
		if (confirmed && this.isCurrentActionRequest(requestId, expectedMarkerSetId)) {
			if (expectedMarkerSetId == null) {
				await this.openMarkerSetModal(MarkerSetModalMode.EDIT_COPY);
				return;
			}

			await this.openMarkerSetModal(MarkerSetModalMode.EDIT_COPY, expectedMarkerSetId);
		}
	}

	private isCurrentLoadRequest (
		requestId: number,
		markerSet: LegacyMarkerSet | Promise<MarkerSet> | null | undefined
	): boolean {
		return !this.isDestroyed &&
			this.markerSetLoadRequestId === requestId &&
			this.markerSet === markerSet;
	}

	private isCurrentActionRequest (requestId: number, expectedMarkerSetId?: number): boolean {
		return !this.isDestroyed &&
			this.markerSetActionRequestId === requestId &&
			this.isExpectedMarkerSet(expectedMarkerSetId);
	}

	private isExpectedMarkerSet (expectedMarkerSetId?: number): boolean {
		return expectedMarkerSetId == null ||
			this.resolvedMarkerSet?.id === expectedMarkerSetId;
	}

	private sortResolvedMarkerTags (): void {
		if (!this.resolvedMarkerSet?.tags) {
			this.markers = [];
			return;
		}

		this.resolvedMarkerSet.tags.sort((a, b) => {
			const sortA = a.sort_index ?? 0;
			const sortB = b.sort_index ?? 0;
			return sortA - sortB;
		});

		this.markers = [...this.resolvedMarkerSet.tags];
	}
}
